import { Menu, TextInput, type TextInputProps } from '@mantine/core'
import { useDebouncedValue, useClickOutside } from '@mantine/hooks'
import { useRef, useEffect, useState } from 'react'

import { type CoreCountryCode, type CoreAddress } from '@flecto/types'
import { useAddressInline, useAddressAutocomplete } from '@flecto/utils'

import { TextAddress } from '../TextAddress/TextAddress'

export type AutocompleteAddressProps = TextInputProps & {
  countryToSearch?: CoreCountryCode
  onSelectAddress?: (address: CoreAddress) => void
}

export function AutocompleteAddress({
  countryToSearch,
  onSelectAddress,
  ...props
}: AutocompleteAddressProps) {
  const [opened, setOpened] = useState(false)
  const refInput = useRef<HTMLInputElement>(null)
  const refTrigger = useClickOutside(() => setOpened(false))
  const search = useAddressAutocomplete()
  const getAddressInline = useAddressInline()
  const [searchText] = useDebouncedValue(String(props.value), 200)
  const [autocompleteAddresses, setAutocompleteAddresses] = useState<
    CoreAddress[]
  >([])

  useEffect(() => {
    search(searchText, { countryToSearch }).then((res) =>
      setAutocompleteAddresses(res)
    )
  }, [searchText, countryToSearch])

  const selectAddress = (address: CoreAddress) => {
    onSelectAddress?.(address)
    setOpened(false)
  }

  return (
    <div ref={refTrigger}>
      <Menu width='target' opened={opened} zIndex={9999}>
        <Menu.Target>
          <TextInput
            {...props}
            ref={refInput}
            type='text'
            onFocus={() => {
              setOpened(true)
              setTimeout(() => refInput.current?.focus(), 50)
            }}
          />
        </Menu.Target>
        <Menu.Dropdown>
          {autocompleteAddresses.map((address, key) => (
            <Menu.Item
              key={`autocomplete-address-${key.toString()}`}
              onClick={() => selectAddress(address)}
              style={{ overflow: 'hidden' }}
              title={getAddressInline(address)}
            >
              <TextAddress address={address} withLabel />
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </div>
  )
}
